import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  Activity, KeyRound, Network, ShieldCheck,
  CheckCircle2, AlertTriangle, Clock, Zap, RefreshCw,
  TrendingUp, Lock, Send, ChevronRight, Cpu, Radio,
  AlertCircle, Play,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';
import api from '../../services/api';

// ─── Types ───────────────────────────────────────────────────────────────────
interface AgentStatus {
  nodeId: string;
  nodeName: string;
  cycleType: 'FIRST' | 'FOLLOW';
  activeSessionId: string | null;
  activeSessionCode: string | null;
  sessionStatus: string | null;
  kpiAutoCollectedAt: string | null;
  kpiAutoNextRun: string | null;
  previewScore: number | null;
  ranking: 'A' | 'B_TOT' | 'B_DAT' | 'C' | null;
  hasHardCondition: boolean;
  ndopStatus: 'ONLINE' | 'OFFLINE' | 'DEGRADED';
  hsmHealthy: boolean;
  lastNdopSubmitAt: string | null;
}

interface KpiAutoValue {
  code: string;
  name: string;
  rawValue: number | null;
  score: number | null;
  collectedAt: string | null;
  status: 'OK' | 'PENDING' | 'ERROR';
}

interface CronJobStatus {
  name: string;
  schedule: string;
  lastRunAt: string | null;
  nextRunAt: string | null;
  lastStatus: 'SUCCESS' | 'FAILED' | 'RUNNING' | null;
  jobId: string | null;
}

// ─── Mock data (replace with API) ────────────────────────────────────────────
const MOCK_STATUS: AgentStatus = {
  nodeId: 'node-sgtt-hn-01',
  nodeName: 'Sở Giao thông Hà Nội',
  cycleType: 'FOLLOW',
  activeSessionId: 'sess-2026-003',
  activeSessionCode: 'KT-2026-HN-003',
  sessionStatus: 'COLLECTING',
  kpiAutoCollectedAt: new Date(Date.now() - 3 * 3600_000).toISOString(),
  kpiAutoNextRun: new Date(new Date().setHours(2, 0, 0, 0) + 86400_000).toISOString(),
  previewScore: 74.2,
  ranking: 'B_TOT',
  hasHardCondition: false,
  ndopStatus: 'ONLINE',
  hsmHealthy: true,
  lastNdopSubmitAt: new Date(Date.now() - 5 * 86400_000).toISOString(),
};

const MOCK_KPI_AUTO: KpiAutoValue[] = [
  { code: 'TC1a', name: 'Tỷ lệ CSDL đã công bố (%)', rawValue: 78, score: 7, collectedAt: MOCK_STATUS.kpiAutoCollectedAt, status: 'OK' },
  { code: 'TC4a', name: 'Tỷ lệ tài sản có bảo vệ (%)', rawValue: 85, score: 8, collectedAt: MOCK_STATUS.kpiAutoCollectedAt, status: 'OK' },
  { code: 'TC4c', name: 'Số lỗ hổng CVSS≥7.0 chưa xử lý', rawValue: 2, score: 7, collectedAt: MOCK_STATUS.kpiAutoCollectedAt, status: 'OK' },
  { code: 'TC5a', name: 'Tỷ lệ log thu thập / yêu cầu (%)', rawValue: 92, score: 9, collectedAt: MOCK_STATUS.kpiAutoCollectedAt, status: 'OK' },
  { code: 'TC5b', name: 'MTTR sự cố bảo mật (giờ)', rawValue: 18, score: 7, collectedAt: MOCK_STATUS.kpiAutoCollectedAt, status: 'OK' },
  { code: 'TC6a', name: 'Uptime CSDL quan trọng (%)', rawValue: 99.2, score: 10, collectedAt: MOCK_STATUS.kpiAutoCollectedAt, status: 'OK' },
  { code: 'TC6b', name: 'Độ trễ phục hồi RTO (phút)', rawValue: 45, score: 7, collectedAt: MOCK_STATUS.kpiAutoCollectedAt, status: 'OK' },
];

const MOCK_CRON: CronJobStatus[] = [
  {
    name: 'KPI AUTO Collector (TTDLQG_AUTO)',
    schedule: '0 2 * * *',
    lastRunAt: MOCK_STATUS.kpiAutoCollectedAt,
    nextRunAt: MOCK_STATUS.kpiAutoNextRun,
    lastStatus: 'SUCCESS',
    jobId: 'job-kpi-2026-0608',
  },
  {
    name: 'NDOP Heartbeat',
    schedule: '*/5 * * * *',
    lastRunAt: new Date(Date.now() - 2 * 60_000).toISOString(),
    nextRunAt: new Date(Date.now() + 3 * 60_000).toISOString(),
    lastStatus: 'SUCCESS',
    jobId: null,
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────
const rankingConfig = {
  A:     { label: 'Loại A', color: 'emerald', desc: '≥ 80 điểm, không vi phạm HC' },
  B_TOT: { label: 'B - Tốt', color: 'blue',   desc: '70–79 điểm' },
  B_DAT: { label: 'B - Đạt', color: 'cyan',   desc: '60–69 điểm' },
  C:     { label: 'Loại C', color: 'rose',    desc: '< 60 điểm hoặc vi phạm HC' },
};

function scoreColor(s: number | null) {
  if (s === null) return 'text-slate-400';
  if (s >= 80) return 'text-emerald-500';
  if (s >= 70) return 'text-blue-500';
  if (s >= 60) return 'text-cyan-500';
  return 'text-rose-500';
}

function timeFromNow(iso: string | null): string {
  if (!iso) return '—';
  const diff = new Date(iso).getTime() - Date.now();
  const abs  = Math.abs(diff);
  if (abs < 60_000)  return diff < 0 ? 'vừa xong' : 'ngay bây giờ';
  if (abs < 3600_000) return `${Math.round(abs / 60_000)} phút ${diff < 0 ? 'trước' : 'nữa'}`;
  if (abs < 86400_000) return `${Math.round(abs / 3600_000)} giờ ${diff < 0 ? 'trước' : 'nữa'}`;
  return `${Math.round(abs / 86400_000)} ngày ${diff < 0 ? 'trước' : 'nữa'}`;
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function AgentDashboard() {
  const qc = useQueryClient();
  const [triggerKpiId, setTriggerKpiId] = useState<string | null>(null);

  // API queries (currently using mock — swap .then with real API)
  const { data: status = MOCK_STATUS } = useQuery<AgentStatus>({
    queryKey: ['agent-status'],
    queryFn: () => api.get('/agent/status').then(r => r.data ?? MOCK_STATUS).catch(() => MOCK_STATUS),
    refetchInterval: 30_000,
  });

  const { data: kpiAuto = MOCK_KPI_AUTO } = useQuery<KpiAutoValue[]>({
    queryKey: ['agent-kpi-auto'],
    queryFn: () => api.get('/collector/kpi-auto/latest').then(r => r.data ?? MOCK_KPI_AUTO).catch(() => MOCK_KPI_AUTO),
    refetchInterval: 60_000,
  });

  const { data: cronJobs = MOCK_CRON } = useQuery<CronJobStatus[]>({
    queryKey: ['agent-cron-jobs'],
    queryFn: () => api.get('/collector/cron/status').then(r => r.data ?? MOCK_CRON).catch(() => MOCK_CRON),
    refetchInterval: 30_000,
  });

  const triggerKpi = useMutation({
    mutationFn: () => api.post('/collector/kpi-auto/trigger'),
    onMutate: () => setTriggerKpiId('running'),
    onSuccess: () => {
      setTriggerKpiId(null);
      qc.invalidateQueries({ queryKey: ['agent-kpi-auto'] });
      toast.success('Đã kích hoạt thu thập KPI AUTO');
    },
    onError: () => { setTriggerKpiId(null); toast.error('Lỗi kích hoạt KPI AUTO'); },
  });

  const ranking = status.ranking ? rankingConfig[status.ranking] : null;
  const kpiCollectedOk = kpiAuto.filter(k => k.status === 'OK').length;

  // ── Stat Cards ──────────────────────────────────────────────────────────────
  const statCards = [
    {
      icon: Activity,
      label: 'Phiên kiểm toán',
      value: status.activeSessionCode ?? 'Chưa có phiên',
      sub:   `${status.cycleType === 'FIRST' ? 'Lần đầu (TC7 max 5.0)' : 'Tái kiểm (TC7 full 10.0)'} · ${status.sessionStatus ?? '—'}`,
      color: 'indigo',
      ok:    !!status.activeSessionId,
      link:  '/agent/sessions',
    },
    {
      icon: TrendingUp,
      label: 'Điểm Preview',
      value: status.previewScore !== null ? `${status.previewScore.toFixed(1)} / 100` : '—',
      sub:   ranking ? `${ranking.label} · ${ranking.desc}` : 'Chưa tính',
      color: status.previewScore !== null && status.previewScore >= 80 ? 'emerald' :
             status.previewScore !== null && status.previewScore >= 60 ? 'blue' : 'rose',
      ok:    !status.hasHardCondition && (status.previewScore ?? 0) >= 60,
      link:  '/agent/kpi-collector',
    },
    {
      icon: Cpu,
      label: 'KPI AUTO (7 chỉ số)',
      value: `${kpiCollectedOk} / 7 đã thu thập`,
      sub:   `Cron 02:00 · Tiếp theo: ${timeFromNow(status.kpiAutoNextRun)}`,
      color: kpiCollectedOk === 7 ? 'emerald' : 'amber',
      ok:    kpiCollectedOk === 7,
      link:  '/agent/kpi-collector',
    },
    {
      icon: Radio,
      label: 'NDOP Gateway',
      value: status.ndopStatus,
      sub:   status.lastNdopSubmitAt ? `Gửi cuối: ${timeFromNow(status.lastNdopSubmitAt)}` : 'Chưa gửi',
      color: status.ndopStatus === 'ONLINE' ? 'emerald' : status.ndopStatus === 'DEGRADED' ? 'amber' : 'rose',
      ok:    status.ndopStatus === 'ONLINE',
      link:  '/agent/connections',
    },
  ];

  return (
    <div className="space-y-10">
      {/* ── Header ── */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight text-slate-900 dark:text-white">
            Agent Node
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 font-medium">
            {status.nodeName} · {status.nodeId}
          </p>
          <div className="flex items-center gap-2 mt-2">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
              status.cycleType === 'FIRST'
                ? 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400'
                : 'bg-indigo-100 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-400'
            }`}>
              {status.cycleType === 'FIRST' ? '⓪ Lần đầu (FIRST)' : '↻ Tái kiểm (FOLLOW)'}
            </span>
            {status.hasHardCondition && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-rose-100 dark:bg-rose-500/15 text-rose-700 dark:text-rose-400">
                <AlertCircle size={10} /> Vi phạm HC — Loại C
              </span>
            )}
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
              status.hsmHealthy
                ? 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
                : 'bg-rose-100 dark:bg-rose-500/15 text-rose-600 dark:text-rose-400'
            }`}>
              <Lock size={10} /> HSM {status.hsmHealthy ? 'OK' : 'ERROR'}
            </span>
          </div>
        </div>
        <button
          onClick={() => triggerKpi.mutate()}
          disabled={!!triggerKpiId}
          className="flex items-center gap-2 px-4 py-3 rounded-2xl bg-indigo-600 text-white font-black text-sm hover:bg-indigo-700 disabled:opacity-50 transition-all shadow-lg shadow-indigo-500/20"
        >
          {triggerKpiId ? <RefreshCw size={16} className="animate-spin" /> : <Play size={16} />}
          {triggerKpiId ? 'Đang thu thập...' : 'Thu thập KPI ngay'}
        </button>
      </div>

      {/* ── Stat Cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
        {statCards.map((card, i) => (
          <motion.div
            key={card.label}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.07 }}
          >
            <Link to={card.link}
              className="block bg-white dark:bg-white/[0.03] border border-slate-200 dark:border-white/5 rounded-3xl p-6 shadow-sm hover:border-indigo-500/40 hover:shadow-md transition-all group">
              <div className="flex items-start justify-between mb-4">
                <div className={`w-11 h-11 rounded-2xl flex items-center justify-center bg-${card.color}-50 dark:bg-${card.color}-500/10`}>
                  <card.icon size={20} className={`text-${card.color}-600 dark:text-${card.color}-400`} />
                </div>
                {card.ok
                  ? <CheckCircle2 size={16} className="text-emerald-500 mt-0.5" />
                  : <AlertTriangle size={16} className="text-amber-500 mt-0.5" />
                }
              </div>
              <p className="text-xs font-black text-slate-400 tracking-wider uppercase mb-1">{card.label}</p>
              <p className="text-base font-black text-slate-900 dark:text-white truncate">{card.value}</p>
              <p className="text-xs text-slate-500 dark:text-slate-500 mt-1 font-medium">{card.sub}</p>
              <ChevronRight size={14} className="mt-3 text-slate-300 group-hover:text-indigo-500 transition-colors" />
            </Link>
          </motion.div>
        ))}
      </div>

      {/* ── Score Preview Bar ── */}
      {status.previewScore !== null && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-white/[0.03] border border-slate-200 dark:border-white/5 rounded-3xl p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-black text-slate-900 dark:text-white flex items-center gap-2">
              <TrendingUp size={16} className="text-indigo-500" />
              Điểm Preview theo 8 Tiêu chí (NĐ 278/2025 · Phụ lục II v3.1)
            </h2>
            <span className={`text-xl font-black tabular-nums ${scoreColor(status.previewScore)}`}>
              {status.previewScore.toFixed(1)}
              <span className="text-sm text-slate-400 font-medium ml-1">/ 100</span>
            </span>
          </div>

          {/* Score bar */}
          <div className="relative h-3 bg-slate-100 dark:bg-white/5 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-700 ${
                status.previewScore >= 80 ? 'bg-emerald-500' :
                status.previewScore >= 70 ? 'bg-blue-500' :
                status.previewScore >= 60 ? 'bg-cyan-500' : 'bg-rose-500'
              }`}
              style={{ width: `${status.previewScore}%` }}
            />
            {/* Threshold markers */}
            {[60, 70, 80].map(t => (
              <div key={t} className="absolute top-0 bottom-0 w-px bg-white/50 dark:bg-black/30" style={{ left: `${t}%` }} />
            ))}
          </div>

          <div className="flex justify-between mt-2 text-[10px] font-black text-slate-400">
            <span className="text-rose-500">C (&lt;60)</span>
            <span className="text-cyan-500">B-Đạt (60-69)</span>
            <span className="text-blue-500">B-Tốt (70-79)</span>
            <span className="text-emerald-500">A (≥80)</span>
          </div>

          {ranking && (
            <div className={`mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-${ranking.color}-100 dark:bg-${ranking.color}-500/15 text-${ranking.color}-700 dark:text-${ranking.color}-400`}>
              <ShieldCheck size={14} />
              <span className="text-xs font-black">{ranking.label} · {ranking.desc}</span>
            </div>
          )}
        </motion.div>
      )}

      {/* ── KPI AUTO Panel ── */}
      <div className="bg-white dark:bg-white/[0.03] border border-slate-200 dark:border-white/5 rounded-3xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-sm font-black text-slate-900 dark:text-white flex items-center gap-2">
            <Cpu size={16} className="text-indigo-500" />
            7 KPI Tự động (TTDLQG_AUTO · Cron 02:00)
          </h2>
          <div className="flex items-center gap-3">
            <span className="text-[10px] text-slate-400 font-medium">
              Thu thập: {status.kpiAutoCollectedAt ? new Date(status.kpiAutoCollectedAt).toLocaleString('vi-VN') : 'Chưa có'}
            </span>
            <Link to="/agent/kpi-collector" className="text-[10px] font-black text-indigo-500 hover:underline">
              Xem chi tiết →
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          {kpiAuto.map((kpi, i) => (
            <motion.div
              key={kpi.code}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.05 }}
              className="bg-slate-50 dark:bg-white/[0.02] rounded-2xl p-4 border border-slate-100 dark:border-white/5"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 bg-indigo-100 dark:bg-indigo-500/20 px-2 py-0.5 rounded-full">
                  {kpi.code}
                </span>
                <span className={`text-[10px] font-black ${
                  kpi.status === 'OK' ? 'text-emerald-500' :
                  kpi.status === 'ERROR' ? 'text-rose-500' : 'text-amber-500'
                }`}>
                  {kpi.status === 'OK' ? '✓ OK' : kpi.status === 'ERROR' ? '✗ ERR' : '⏳'}
                </span>
              </div>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-snug mb-2">{kpi.name}</p>
              <div className="flex items-end justify-between">
                <span className="text-sm font-black text-slate-700 dark:text-slate-200">
                  {kpi.rawValue !== null ? kpi.rawValue.toLocaleString('vi-VN') : '—'}
                </span>
                <span className={`text-base font-black tabular-nums ${
                  (kpi.score ?? 0) >= 8 ? 'text-emerald-500' :
                  (kpi.score ?? 0) >= 5 ? 'text-amber-500' : 'text-rose-500'
                }`}>
                  {kpi.score ?? '—'}<span className="text-[10px] text-slate-400">/10</span>
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* ── Cron Jobs & NDOP ── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Cron jobs */}
        <div className="bg-white dark:bg-white/[0.03] border border-slate-200 dark:border-white/5 rounded-3xl p-6">
          <h2 className="text-sm font-black text-slate-900 dark:text-white mb-5 flex items-center gap-2">
            <Clock size={16} className="text-indigo-500" /> Cron Jobs & Bull Queue
          </h2>
          <div className="space-y-3">
            {cronJobs.map(job => (
              <div key={job.name} className="flex items-center gap-4 p-4 rounded-2xl bg-slate-50 dark:bg-white/[0.02] border border-slate-100 dark:border-white/5">
                <div className={`w-2 h-2 rounded-full shrink-0 ${
                  job.lastStatus === 'SUCCESS' ? 'bg-emerald-500 animate-pulse' :
                  job.lastStatus === 'FAILED'  ? 'bg-rose-500' :
                  job.lastStatus === 'RUNNING' ? 'bg-amber-500 animate-pulse' : 'bg-slate-300'
                }`} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-black text-slate-900 dark:text-white truncate">{job.name}</p>
                  <p className="text-[10px] font-mono text-slate-400 mt-0.5">{job.schedule}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[10px] text-slate-500">Lần trước: {timeFromNow(job.lastRunAt)}</p>
                  <p className="text-[10px] text-indigo-500 font-bold">Tiếp theo: {timeFromNow(job.nextRunAt)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick actions */}
        <div className="bg-white dark:bg-white/[0.03] border border-slate-200 dark:border-white/5 rounded-3xl p-6">
          <h2 className="text-sm font-black text-slate-900 dark:text-white mb-5 flex items-center gap-2">
            <Zap size={16} className="text-amber-500" /> Thao tác nhanh
          </h2>
          <div className="space-y-2">
            {[
              { label: 'Thu thập 7 KPI AUTO ngay', desc: 'Chạy cron thủ công, không đợi 02:00', icon: Cpu, action: () => triggerKpi.mutate(), color: 'indigo' },
              { label: 'Xem Phiếu KPI', desc: 'Tự khai 20 KPI còn lại', icon: Activity, href: '/agent/kpi-collector', color: 'blue' },
              { label: 'Gửi báo cáo lên NDOP', desc: 'Preview score → Submit NDOP', icon: Send, href: '/agent/reports', color: 'emerald' },
              { label: 'Kiểm tra kết nối NDOP', desc: 'Ping + circuit breaker status', icon: Network, href: '/agent/connections', color: 'slate' },
              { label: 'Quản lý Key RSA-4096', desc: 'Xem trạng thái keypair & DSCert', icon: KeyRound, href: '/agent/keys', color: 'amber' },
              { label: 'Giám sát bảo mật', desc: 'CVSS events, HC1 conditions', icon: ShieldCheck, href: '/agent/security', color: 'rose' },
            ].map((item) => (
              item.href ? (
                <Link key={item.label} to={item.href}
                  className="flex items-center gap-4 p-3 rounded-xl hover:bg-slate-50 dark:hover:bg-white/[0.03] border border-transparent hover:border-slate-100 dark:hover:border-white/5 transition-all group"
                >
                  <div className={`w-9 h-9 rounded-xl bg-${item.color}-100 dark:bg-${item.color}-500/15 flex items-center justify-center shrink-0`}>
                    <item.icon size={16} className={`text-${item.color}-600 dark:text-${item.color}-400`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-black text-slate-900 dark:text-white">{item.label}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{item.desc}</p>
                  </div>
                  <ChevronRight size={14} className="text-slate-300 group-hover:text-indigo-500 transition-colors" />
                </Link>
              ) : (
                <button key={item.label}
                  onClick={item.action}
                  disabled={!!triggerKpiId}
                  className="w-full flex items-center gap-4 p-3 rounded-xl hover:bg-slate-50 dark:hover:bg-white/[0.03] border border-transparent hover:border-slate-100 dark:hover:border-white/5 transition-all group disabled:opacity-50"
                >
                  <div className={`w-9 h-9 rounded-xl bg-${item.color}-100 dark:bg-${item.color}-500/15 flex items-center justify-center shrink-0`}>
                    <item.icon size={16} className={`text-${item.color}-600 dark:text-${item.color}-400 ${triggerKpiId ? 'animate-spin' : ''}`} />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-xs font-black text-slate-900 dark:text-white">{item.label}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{item.desc}</p>
                  </div>
                  <ChevronRight size={14} className="text-slate-300 group-hover:text-indigo-500 transition-colors" />
                </button>
              )
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
